import React from "react";

import {
    Box,
    Step,
    StepLabel,
    Stepper,
    Typography
} from "@mui/material";
import { StepIconProps } from "@mui/material/StepIcon";
import Check from "@mui/icons-material/Check";

import { t, Trans, } from "@lingui/macro";
import { WithSnackbarProps, withSnackbar } from "notistack";
import { ContentProducerService } from "services/content-producer-service";

import { ContentUploadResume, CreateAndEditContentDetailForm, SegregationForm } from "components/backoffice/content";

import { CreateContentState } from "./models";
import { ErrorResponseApi, GetCollectionListResponse } from "models/response";
import { UploadContentRequest } from "models/request";

import { ContentType, SegregationType } from "commons/form-constants";
import { ZPlayNetworkService } from "commons/zplay-network-service";
import { NavigationBack, ZPlayUploadLoader, ZPlayLoader } from "commons";
import { ZipPlayStepperConnector, ZipPlayStepIconRoot } from "commons/commons";
import { S3MultipartUploadData, UploadContentResponse, VODUploadType } from "models/request/upload-content-response";

import { AWSError, S3 } from "aws-sdk";
import { AbortMultipartUploadRequest, UploadPartOutput, UploadPartRequest } from "aws-sdk/clients/s3";
import { PromiseResult } from "aws-sdk/lib/request";
import { MultipartConfirmUploadRequest, VODPartConfirmData } from "models/request/multipart-confirm-upload-request";

const MULTIPART_PART_SIZE_IN_MB = 25;
const MULTIPART_RETRIES = 8;
class CreateContent extends React.Component<WithSnackbarProps, CreateContentState> {

    private s3;
    private multipartUploadedPartsETagList?: VODPartConfirmData[];
    private multipartNotUploadedPartList: { partNumber: number, start: number, end: number }[];
    private uploadType: VODUploadType;
    private totalParts: number;
    private partsUploaded: number;
    private alreadyUsedSubtitlesUrl: string[];

    constructor(props: any) {

        super(props);

        this.s3 = new S3({
            credentials: {
                accessKeyId: process.env.REACT_APP_AWS_ROLE_AKID ?? "",
                secretAccessKey: process.env.REACT_APP_AWS_ROLE_SECRET ?? ""
            },
            region: "eu-central-1"
        });

        this.alreadyUsedSubtitlesUrl = [];
        this.multipartUploadedPartsETagList = [];
        this.multipartNotUploadedPartList = [];
        this.uploadType = VODUploadType.DIRECT;
        this.totalParts = 0;
        this.partsUploaded = 0;

        this.state = {
            activeStep: 0,
            currentUploadRequest: null,
            thumbnailFile: null,
            subtitlesForUpload: null,
            isUploading: false,
            thumbUploadUrl: null,
            uploadType: null,
            vodUploadUrl: null,
            vodFile: null,
            subtitlesUploadUrl: [],
            hasFinalError: false,
            currentUploadPercentage: 0,
            uploadStageMessage: "",
            collectionsList: null,
            isLoading: true
        };
    }

    public componentDidMount() {
        ContentProducerService.getCollectionList(this.getCollectionListSuccessCallback, this.getCollectionListErrorCallback);
    };

    getCollectionListSuccessCallback = (data: GetCollectionListResponse) => {
        this.setState(() => ({
            collectionsList: data,
            isLoading: false
        }))
    }

    getCollectionListErrorCallback = (err: ErrorResponseApi) => {

        let emptyCollectionList: GetCollectionListResponse = {
            awardList: { list: [], size: 0 },
            countryList: { list: [], size: 0 },
            festivalList: { list: [], size: 0 },
            genreList: { list: [], size: 0 },
            languageList: { list: [], size: 0 },
            tagList: { list: [], size: 0 },
            contentIssuesList: { list: [], size: 0 },
            seriesList: { list: [], size: 0 }
        };

        this.setState(() => ({
            isLoading: false,
            collectionsList: emptyCollectionList,
        }));

        this.props.enqueueSnackbar(t({
            id: "cu-error-get-collection-list-error",
            message: "Si è verificato un errore. Non è stato possibile recuperare i dati."
        }), {
            variant: "error",
        });
    }

    private getSegregationData = (stepData: any) => {

        let currentRequest = this.state.currentUploadRequest;

        if (!currentRequest)
            return

        currentRequest.segregationType = stepData.segregationType;

        if (currentRequest.segregationType === SegregationType.PAY_PER_VIEW) {
            currentRequest.payPerViewSegregationAdditionalInfos = {
                price: stepData.price,
                currency: stepData.currency
            };
        }

        this.setState(() => ({
            currentUploadRequest: currentRequest,
        }));

        this.startUpload();
    };

    private checkDetailsStepValidity = (stepData: any): boolean => {

        let errorsArray: string[] = [];
        let warningArray: string[] = [];

        if (stepData.title.length === 0)
            errorsArray.push(t({
                id: "cu-form-validation-no-title",
                message: "È necessario inserire almeno un titolo"
            }));

        if (stepData.synopsis.length === 0)
            errorsArray.push(t({
                id: "cu-form-validation-no-synopsis",
                message: "È necessario inserire almeno una sinossi"
            }));

        if (!stepData.thumbnailFile)
            errorsArray.push(t({
                id: "cu-form-validation-no-thumbnail-file",
                message: "Non è stata caricata un'immagine di copertina"
            }));

        if (!stepData.language)
            errorsArray.push(t({
                id: "cu-form-validation-no-language",
                message: "Non è stato caricato nessuna lingua del film"
            }));

        if (stepData.seriesData && !stepData.seriesData.seriesId)
            errorsArray.push(t({
                id: "ec-form-validation-serie-err",
                message: "Serie non indicata"
            }));

        if (stepData.seriesData && !Number(stepData.seriesData.episodeNumber))
            errorsArray.push(t({
                id: "ec-form-validation-episode-err",
                message: "Episodio non indicato o indicato in maniera errata"
            }));

        if (stepData.seriesData && !Number(stepData.seriesData.season))
            errorsArray.push(t({
                id: "ec-form-validation-season-err",
                message: "Stagione non indicata o indicata in maniera errata"
            }));

        if (!Number(stepData.productionYear) && stepData.productionYear !== "0")
            errorsArray.push(t({
                id: "cu-form-validation-no-production-year",
                message: "L'anno di produzione non è valido"
            }));

        if (stepData.director.length === 0)
            warningArray.push(t({
                id: "cu-form-validation-no-director",
                message: "Non è stato indicato nessun regista"
            }));

        if (!stepData.type)
            errorsArray.push(t({
                id: "cu-form-validation-no-type",
                message: "È necessario definire un tipo contenuto"
            }));

        if (!Number(stepData.durationInSeconds))
            errorsArray.push(t({
                id: "ec-form-validation-no-duration",
                message: "Durata del film non indicata o indicata in maniera errata"
            }));

        if (warningArray.length > 0)
            this.props.enqueueSnackbar(warningArray.join(". "),
                { variant: "warning" });

        if (errorsArray.length > 0)
            this.props.enqueueSnackbar(errorsArray.join(". "),
                { variant: "error" });

        return errorsArray.length === 0;
    }

    private getDetailData = (stepData: any) => {

        if (!this.checkDetailsStepValidity(stepData))
            return;

        let currentRequest: UploadContentRequest = {
            title: [],
            synopsis: [],
            vodMimeType: "",
            dialogue: false,
            language: "",
            genre: [],
            director: [],
            producer: [],
            cast: [],
            writer: [],
            composer: [],
            dop: [],
            editor: [],
            festivals: [],
            awards: [],
            country: "",
            productionYear: "",
            music: false,
            color: false,
            academyQualified: [],
            tagList: [],
            needsEncoding: false,
            durationInSeconds: 0
        };

        currentRequest.title = stepData.title;
        currentRequest.synopsis = stepData.synopsis;
        currentRequest.type = stepData.type;
        currentRequest.tagList = stepData.tagList;
        currentRequest.releaseDate = stepData.releaseDate || new Date();
        currentRequest.director = stepData.director;
        currentRequest.academyQualified = stepData.academyQualified;
        currentRequest.language = stepData.language;
        currentRequest.producer = stepData.producer;
        currentRequest.cast = stepData.cast;
        currentRequest.writer = stepData.writer;
        currentRequest.composer = stepData.composer;
        currentRequest.dop = stepData.dop;
        currentRequest.editor = stepData.editor;
        currentRequest.festivals = stepData.festivals;
        currentRequest.awards = stepData.awards;
        currentRequest.dialogue = stepData.dialogue;
        currentRequest.music = stepData.music;
        currentRequest.musicCueSheet = stepData.musicCueSheet;
        currentRequest.country = stepData.country;
        currentRequest.productionYear = stepData.productionYear;
        currentRequest.releaseDate = stepData.releaseDate;
        currentRequest.genre = stepData.genre;
        currentRequest.ageClassification = stepData.ageClassification;
        currentRequest.contentIssues = stepData.contentIssues;
        currentRequest.color = stepData.color;
        currentRequest.type = stepData.type;
        currentRequest.durationInSeconds = stepData.durationInSeconds;

        let thumbnailFile = stepData.thumbnailFile;

        currentRequest.thumbnailData = {
            fileName: thumbnailFile.name,
            format: thumbnailFile.type.split("/")[1]
        };

        currentRequest.subtitles = [];

        let subtitles = stepData.subtitles;

        if (subtitles) {
            for (let subtitle of subtitles) {
                currentRequest.subtitles?.push({
                    fileName: subtitle.fileName,
                    language: subtitle.language,
                    format: subtitle.file.type.split("/")[1]
                });
            }
        }

        let vodFile: any = stepData.vodFile;

        if (vodFile && currentRequest.type === ContentType.VOD) {

            currentRequest.needsEncoding = stepData.needsEncoding;

            currentRequest.vodMimeType = vodFile.type;
            currentRequest.fileSize = vodFile.size;

            currentRequest.vodAdditionalInfos = {
                fileName: vodFile.name,
                format: vodFile.type.split("/")[1]
            };

        }

        if (currentRequest.type === ContentType.LIVE) {

            currentRequest.liveAdditionalInfos = {
                eventEndDateTime: stepData.eventEndDateTime,
                eventStartDateTime: stepData.eventStartDateTime
            };

        }

        this.setState(() => ({
            currentUploadRequest: currentRequest,
            vodFile: vodFile,
            subtitlesForUpload: subtitles,
            activeStep: 1,
            thumbnailFile: thumbnailFile,
        }));

    };

    private backToStart = () => {
        this.setState(() => ({ activeStep: 0 }));
    }

    private stepIcon(props: StepIconProps) {

        const {
            active,
            completed,
            className
        } = props;

        return (<ZipPlayStepIconRoot ownerState={{ active }} className={className}>
            {completed ? (<Check className="QontoStepIcon-completedIcon" />) : (
                <div className="QontoStepIcon-circle" />)}
        </ZipPlayStepIconRoot>);
    }

    private getCurrentStepContent = () => {

        switch (this.state.activeStep) {
            case 0:
                return (<CreateAndEditContentDetailForm
                    onDetailSubmit={this.getDetailData}
                    content={null}
                    collectionsList={this.state.collectionsList}
                    musicCueSheetsList={[]}
                />);
            case 1:
                return (<SegregationForm onSegregationSubmit={this.getSegregationData}
                    onSegregationBack={this.backToStart} />);
            case 2:
                return (<ContentUploadResume error={this.state.hasFinalError} backToContentUpload={this.backToStart} />);
        }
    };

    private startUpload = () => {

        this.setState(() => ({
            isUploading: true,
            uploadStageMessage: t({
                id: "cu-upload-pre-stage",
                message: "Preparazione al caricamento"
            })
        }));

        ContentProducerService.startContentUpload(this.state.currentUploadRequest, this.startFileUpload,
            this.errorCallback);

    };

    private errorCallback = (err: any) => {
        this.props.enqueueSnackbar(t({
            id: "cc-error-submit-update-content-error",
            message: "Si è verificato un errore. Non è stato possibile caricare il contenuto"
        }), {
            variant: "error",
        });
        this.setState(() => ({ isUploading: false }));
    };

    private startFileUpload = (data: UploadContentResponse) => {

        this.uploadType = data.uploadType;

        this.setState(() => ({
            uploadedContent: data.content,
            uploadType: data.uploadType,
            vodUploadUrl: (data.uploadType === VODUploadType.DIRECT && data.s3PresignedUploadUrl) ? data.s3PresignedUploadUrl : null,
            vodMultipartUploadData: data.uploadType === VODUploadType.MULTIPART ? data.s3MultipartUploadData : null,
            thumbUploadUrl: data.s3PresignedThumbnailUrl,
            subtitlesUploadUrl: data.s3PresignedSubtitlesFileList,
            uploadStageMessage: t({
                id: "cu-upload-content-stage",
                message: "Caricamento del contenuto in corso..."
            })
        }));

        this.startThumbUpload();
    };

    private startVODNetworkUploadProcess = () => {

        this.setState(() => ({
            currentUploadPercentage: 0,
            uploadStageMessage: t({
                id: "cu-upload-vod-stage",
                message: "Caricamento del video in corso..."
            })
        }));

        if (this.state.uploadType === VODUploadType.DIRECT && this.state.vodUploadUrl) {

            if (this.state.currentUploadRequest?.subtitles && this.state.currentUploadRequest.subtitles.length > 0) {

                ZPlayNetworkService.xhrMultipartUpload(this.state.vodUploadUrl, this.state.vodFile, this.progressCallback)
                    .then(this.startSubtitlesUpload)
                    .catch(this.errorCallback)

            } else {
                ZPlayNetworkService.xhrMultipartUpload(this.state.vodUploadUrl, this.state.vodFile, this.progressCallback)
                    .then(this.confirmFileUpload)
                    .catch(this.errorCallback)
            }
        }

        if (this.state.uploadType === VODUploadType.MULTIPART && this.state.vodFile && this.state.vodMultipartUploadData) {
            this.uploadParts(this.state.vodMultipartUploadData);
        }
    }

    private retryNotUploadedParts = (retryCount: number, data: S3MultipartUploadData) => {

        let uploadPromises = [];

        if (retryCount >= MULTIPART_RETRIES) {
            this.props.enqueueSnackbar(t({
                id: "cu-error-upload-multipart",
                message: "Si è verificato un errore durante il caricamento"
            }), {
                variant: "error",
            });
            return
        }


        let file = this.state.vodFile

        if (!file) {
            this.props.enqueueSnackbar(t({
                id: "cu-error-upload-multipart",
                message: "Si è verificato un errore durante il caricamento"
            }), {
                variant: "error",
            });
            console.log("No VOD File")
        } else {

            let i = 0;
            for (const element of this.multipartNotUploadedPartList) {

                let partStart = element.start
                let partEnd = element.end

                let part = (partEnd < file.size) ? file.slice(partStart, partEnd) : file.slice(partStart, file.size);

                let currentPartNumber: number = element.partNumber;

                let s3UploadPartData: UploadPartRequest = {
                    Bucket: data.bucket,
                    Key: data.fileKey,
                    PartNumber: currentPartNumber,
                    UploadId: data.uploadId,
                    Body: part
                }

                let uploadPromise = this.s3.uploadPart(s3UploadPartData).promise();

                uploadPromise.then((data: PromiseResult<UploadPartOutput, AWSError>) => {

                    this.partsUploaded++;

                    this.setState(() => ({
                        currentUploadPercentage: Math.round((this.partsUploaded / this.totalParts) * 100)
                    }))

                    this.multipartUploadedPartsETagList?.push({
                        PartNumber: currentPartNumber,
                        ETag: data["ETag"]
                    })

                    this.multipartNotUploadedPartList.splice(i, 1);
                })

                uploadPromises.push(uploadPromise)
            }

            Promise.allSettled(uploadPromises)
                .then(() => {
                    if (this.multipartNotUploadedPartList.length !== 0)
                        this.retryNotUploadedParts(++retryCount, data);
                    else {
                        if (this.state.currentUploadRequest?.subtitles && this.state.currentUploadRequest.subtitles.length > 0) {
                            this.startSubtitlesUpload({});
                        } else {
                            this.confirmFileUpload({});
                        }
                    }
                })
                .catch(this.onMultipartUploadError)
        }


    }

    private uploadParts = (data: S3MultipartUploadData) => {

        let file = this.state.vodFile

        if (!file) {

            this.props.enqueueSnackbar(t({
                id: "cu-error-upload-multipart",
                message: "Si è verificato un errore durante il caricamento"
            }), {
                variant: "error",
            });

            console.log("No VOD File")
        } else {

            const fileSize = file.size;
            const partSize = (1024 * 1024) * MULTIPART_PART_SIZE_IN_MB
            this.totalParts = Math.ceil(fileSize / partSize)

            let uploadPromises: Promise<PromiseResult<S3.UploadPartOutput, AWSError>>[] = []

            let partStart = 0;

            for (let i = 0; i < this.totalParts; i++) {

                let partStartForCallback = partStart
                let partEnd = partStart + partSize

                let part = (partEnd < file.size) ? file.slice(partStart, partEnd) : file.slice(partStart, file.size);

                let currentPartNumber: number = i + 1;

                let s3UploadPartData: UploadPartRequest = {
                    Bucket: data.bucket,
                    Key: data.fileKey,
                    PartNumber: currentPartNumber,
                    UploadId: data.uploadId,
                    Body: part
                }

                let uploadPromise = this.s3.uploadPart(s3UploadPartData).promise();

                uploadPromise.then((data: PromiseResult<UploadPartOutput, AWSError>) => {

                    this.partsUploaded++;

                    this.setState(() => ({
                        currentUploadPercentage: Math.round((this.partsUploaded / this.totalParts) * 100)
                    }))

                    this.multipartUploadedPartsETagList?.push({
                        PartNumber: currentPartNumber,
                        ETag: data["ETag"]
                    })
                })

                uploadPromise.catch(() => {
                    this.multipartNotUploadedPartList.push(
                        {
                            partNumber: currentPartNumber,
                            start: partStartForCallback,
                            end: partEnd
                        }
                    );
                });

                uploadPromises.push(uploadPromise);

                partStart = partEnd;
            }

            Promise.allSettled(uploadPromises)
                .then((results) => {
                    if (this.multipartNotUploadedPartList.length !== 0)
                        this.retryNotUploadedParts(0, data);
                    else {

                        if (this.state.currentUploadRequest?.subtitles && this.state.currentUploadRequest.subtitles.length > 0) {
                            this.startSubtitlesUpload({});
                        } else {
                            this.confirmFileUpload({});
                        }
                    }
                })
        }

    }


    private onMultipartUploadError = () => {

        if (this.state.vodMultipartUploadData) {

            let abortMultipartRequest: AbortMultipartUploadRequest = {
                Bucket: this.state.vodMultipartUploadData.bucket,
                Key: this.state.vodMultipartUploadData.fileKey,
                UploadId: this.state.vodMultipartUploadData.uploadId
            }

            this.s3.abortMultipartUpload(abortMultipartRequest).promise()
                .then(
                    () => console.log("Successfully aborted Multipart upload"),
                    err => { console.log(`Error ${JSON.stringify(err)}`) }
                );

        }

        this.setState(() => ({
            isUploading: false
        }));

        this.props.enqueueSnackbar(t({
            id: "cu-error-upload-multipart",
            message: "Si è verificato un errore durante il caricamento"
        }), {
            variant: "error",
        });
    }

    private startSubtitlesUpload = (data: any) => {
        this.subtitlesUploadRecursiveInternal(this.state.subtitlesForUpload[0], 0)
    }



    private subtitlesUploadRecursiveInternal = (subtitleToUpload: any, index: any) => {

        if (subtitleToUpload && index <= this.state.subtitlesForUpload.length) {

            this.setState(() => ({
                uploadStageMessage: `${t({
                    id: "cu-upload-subtitles-stage",
                    message: "Caricamento dei sottotitoli per la lingua"
                })}:${subtitleToUpload.language}`
            }));

            let subtitleLink = this.checkWichUrlUseToUploadSubtitle(subtitleToUpload.language);

            if (subtitleLink !== "" && subtitleLink)
                ZPlayNetworkService.xhrMultipartUpload(subtitleLink, subtitleToUpload.file, this.progressCallback)
                    .then((resp: any) => { let newIndex = ++index; this.subtitlesUploadRecursiveInternal(this.state.subtitlesForUpload[newIndex], newIndex) })
                    .catch(this.errorCallback)
            else {
                let newIndex = ++index;
                this.subtitlesUploadRecursiveInternal(this.state.subtitlesForUpload[newIndex], newIndex)
            }

        } else {
            this.confirmFileUpload({});
        }
    }

    private extractId = (url: string) => {
        const regex = /(?<=subtitles\/)\d+/;
        const match = url.match(regex);
        return match ? match[0] : null;
    }
    
    private checkWichUrlUseToUploadSubtitle = (subtitleLanguage: string) => {

        let correctSubtitleUrl: string = ""

        if (this.state.subtitlesUploadUrl && this.state.subtitlesUploadUrl.length > 0) {

            let timestamp = this.extractId(this.state.subtitlesUploadUrl[0])

            for (let subtitleUrl of this.state.subtitlesUploadUrl) {

                if (subtitleUrl.includes(`${subtitleLanguage}-${timestamp}`) && !this.alreadyUsedSubtitlesUrl.includes(subtitleUrl)) {
                   
                    correctSubtitleUrl = subtitleUrl;
                    this.alreadyUsedSubtitlesUrl.push(correctSubtitleUrl);
                } 
            }
        }

        return correctSubtitleUrl;
    }

    private progressCallback = (percentage: number) => {
        this.setState(() => ({ currentUploadPercentage: percentage }))
    }

    private startThumbUpload = () => {

        this.multipartUploadedPartsETagList = [];
        this.totalParts = 0;
        this.partsUploaded = 0;

        this.setState(() => ({
            currentUploadPercentage: 0,
            uploadStageMessage: t({
                id: "cu-upload-thumbnail-stage",
                message: "Caricamento della thumbnail in corso..."
            })
        }));

        ZPlayNetworkService.xhrMultipartUpload(this.state.thumbUploadUrl ?? "", this.state.thumbnailFile, this.progressCallback)
            .then(this.confirmThumbUpload)
            .catch(this.errorCallback)
    };



    private thumbUploadUploadEnd = () => {

        if (!this.state.vodFile) {
            this.uploadEnd();
        }

        if (this.state.vodFile && this.state.uploadedContent?.type === ContentType.VOD) {
            this.startVODNetworkUploadProcess();
        }

        if (this.state.uploadedContent?.type === ContentType.LIVE) {
            this.uploadEnd();
        }
    };

    private uploadEnd = () => {

        this.setState(() => ({
            activeStep: 2,
            isUploading: false
        }));

    };

    private confirmThumbUpload = () => {

        this.setState(() => ({
            currentUploadPercentage: 0
        }));

        if (this.state.uploadedContent)
            ContentProducerService.confirmThumbnailUpload(this.state.uploadedContent.id, this.thumbUploadUploadEnd,
                this.errorCallback);
    };

    private confirmFileUpload = (data: any) => {

        if (this.state.uploadedContent) {

            let requestBody: MultipartConfirmUploadRequest | {}

            if (this.uploadType === VODUploadType.MULTIPART) {
                requestBody = {
                    partList: this.multipartUploadedPartsETagList
                }
            } else {
                requestBody = {}
            }

            ContentProducerService.confirmVODFileUpload(this.state.uploadedContent.id, requestBody, this.uploadEnd,
                this.errorCallback);
        }
    };

    render() {
        return <Box sx={{ p: 5 }}>
            {this.state.isLoading && <ZPlayLoader />}
            <NavigationBack />
            <Box maxWidth={"1000px"} padding={"30px 10px 10px"} margin={"0 auto"}>
                {this.state.isUploading &&
                    <ZPlayUploadLoader progress={this.state.currentUploadPercentage}
                        message={this.state.uploadStageMessage} />}
                <Stepper activeStep={this.state.activeStep} connector={<ZipPlayStepperConnector />}
                    sx={{
                        pb: 5,
                    }} alternativeLabel>
                    <Step key={0}>
                        <StepLabel StepIconComponent={this.stepIcon}>
                            <Typography color={"#FFFFFF"}>
                                <Trans id="cu-details">Dettagli</Trans>
                            </Typography>
                        </StepLabel>
                    </Step>
                    <Step>
                        <StepLabel StepIconComponent={this.stepIcon}>
                            <Typography color={"#FFFFFF"}>
                                <Trans id="cu-segregation">Segregazione</Trans>
                            </Typography>
                        </StepLabel>
                    </Step>
                    <Step>
                        <StepLabel StepIconComponent={this.stepIcon}>
                            <Typography color={"#FFFFFF"}>
                                <Trans id="cu-resume">Riepilogo</Trans>
                            </Typography>
                        </StepLabel>
                    </Step>
                </Stepper>
                {!this.state.isLoading && <Box>
                    {this.getCurrentStepContent()}
                </Box>}
            </Box>
        </Box>;
    }
}

export default withSnackbar(CreateContent);